/**
 * 拖拉拽页图表计算 · 下传 transform（wire Dto → 薄 Vo）。
 *
 * 按 {@link IAnalysisChartResultVoMap} handler 分派；各 chartType 只 pick 前端消费字段。
 *
 * @see ../TRANSFORM-BOUNDARY.md
 */

import { ChartType } from '@dbl/core/chart';

import type {
  IAnalysisChartResponseDtoMap,
  IAnalysisChartResultVoMap,
  TAnalysisChartMapKey,
} from '../types';

import { transformBarChartResponseDtoToVo } from './bar';
import { transformBigNumberChartResponseDtoToVo } from './bigNumber';
import { transformDimNumberTrendChartResponseDtoToVo } from './dimNumberTrend';
import { transformEventLineChartResponseDtoToVo } from './eventLine';
import { transformHorizontalBarChartResponseDtoToVo } from './horizontalBar';
import { transformLineChartResponseDtoToVo } from './line';
import { transformMapChartResponseDtoToVo } from './map';
import { transformMixedChartResponseDtoToVo } from './mixedChart';
import { transformPercentStackBarChartResponseDtoToVo } from './percentStackBar';
import { transformPercentLineChartResponseDtoToVo } from './percentLine';
import { transformPieChartResponseDtoToVo } from './pie';
import { transformStackBarChartResponseDtoToVo } from './stackBar';
import { transformTableChartResponseDtoToVo } from './table';
import { transformTrendChartResponseDtoToVo } from './trend';
import { transformWordCloudChartResponseDtoToVo } from './wordCloud';

export { normalizeResponseDto, parseAxisChartDataVo } from './common';
export { transformBarChartResponseDtoToVo } from './bar';
export { transformBigNumberChartResponseDtoToVo } from './bigNumber';
export { transformDimNumberTrendChartResponseDtoToVo } from './dimNumberTrend';
export { transformEventLineChartResponseDtoToVo } from './eventLine';
export { transformHorizontalBarChartResponseDtoToVo } from './horizontalBar';
export { transformLineChartResponseDtoToVo } from './line';
export { transformMapChartResponseDtoToVo } from './map';
export { transformMixedChartResponseDtoToVo } from './mixedChart';
export { transformPercentStackBarChartResponseDtoToVo } from './percentStackBar';
export { transformPercentLineChartResponseDtoToVo } from './percentLine';
export { transformPieChartResponseDtoToVo } from './pie';
export { transformStackBarChartResponseDtoToVo } from './stackBar';
export { transformTableChartResponseDtoToVo } from './table';
export { transformTrendChartResponseDtoToVo } from './trend';
export { transformWordCloudChartResponseDtoToVo } from './wordCloud';

const transformResponseHandlers: {
  [K in TAnalysisChartMapKey]: (
    dto: IAnalysisChartResponseDtoMap[K],
  ) => IAnalysisChartResultVoMap[K];
} = {
  [ChartType.Table]: transformTableChartResponseDtoToVo,
  [ChartType.Pie]: transformPieChartResponseDtoToVo,
  [ChartType.Bar]: transformBarChartResponseDtoToVo,
  [ChartType.StackBar]: transformStackBarChartResponseDtoToVo,
  [ChartType.HorizontalBarChart]: transformHorizontalBarChartResponseDtoToVo,
  [ChartType.PercentStackBar]: transformPercentStackBarChartResponseDtoToVo,
  [ChartType.PercentLine]: transformPercentLineChartResponseDtoToVo,
  [ChartType.Line]: transformLineChartResponseDtoToVo,
  [ChartType.MixedChart]: transformMixedChartResponseDtoToVo,
  [ChartType.BigNumber]: transformBigNumberChartResponseDtoToVo,
  [ChartType.Trend]: transformTrendChartResponseDtoToVo,
  [ChartType.DimNumberTrend]: transformDimNumberTrendChartResponseDtoToVo,
  [ChartType.Map]: transformMapChartResponseDtoToVo,
  [ChartType.WordCloud]: transformWordCloudChartResponseDtoToVo,
  [ChartType.EventLine]: transformEventLineChartResponseDtoToVo,
};

/**
 * 分析页图表计算 · 下传 transform 主入口（Dto → Vo）。
 *
 * 入参为 {@link IAnalysisChartResponseDtoMap} 已收窄 Dto；runtime 校验在
 * {@link calculateAnalysisChart} 经 {@link normalizeResponseDto} 完成。
 */
export function transformAnalysisChartResponseDtoToVo<
  K extends TAnalysisChartMapKey,
>(
  chartType: K,
  dto: IAnalysisChartResponseDtoMap[K],
): IAnalysisChartResultVoMap[K] {
  return transformResponseHandlers[chartType](dto);
}
